import * as storage from 'src/lib/storage';

interface StorageOptions {
  session?: boolean;
}

interface StringOption {
  read: (defaultValue?: string | null) => string | null;
  write: (value: string) => void;
  remove: () => void;
}

interface BooleanOption {
  read: (defaultValue?: boolean | null) => boolean | null;
  write: (value: boolean) => void;
  remove: () => void;
}

interface CurrentCustomerIdOptions {
  skipSession?: boolean;
}

const createStringOption = (key: string, options?: StorageOptions): StringOption => ({
  read: (defaultValue: string | null = null) => {
    const value = storage.readItem(key, options);
    if (value == null) {
      return defaultValue;
    }
    return value;
  },

  write: (value: string) => {
    storage.writeItem(key, value, options);
  },

  remove: () => {
    storage.removeItem(key, options);
  },
});

const VALUE_TRUE = '1';
const VALUE_FALSE = '0';
const createBooleanOption = (key: string, options?: StorageOptions): BooleanOption => ({
  read: (defaultValue: boolean | null = null) => {
    const value = storage.readItem(key, options);
    if (value == null) {
      return defaultValue;
    }
    return value === VALUE_TRUE;
  },

  write: (value: boolean) => {
    storage.writeItem(key, value ? VALUE_TRUE : VALUE_FALSE, options);
  },

  remove: () => {
    storage.removeItem(key, options);
  },
});

const KEY_CURRENT_CUSTOMER_ID = 'kcc.user.currentCustomerId';
export const currentCustomerId = {
  read: (options?: CurrentCustomerIdOptions) => {
    const sessionValue = !options?.skipSession
      ? storage.readItem(KEY_CURRENT_CUSTOMER_ID, { session: true })
      : null;
    const localStorageValue = storage.readItem(KEY_CURRENT_CUSTOMER_ID);
    return sessionValue || localStorageValue || null;
  },

  write: (value: string) => {
    storage.writeItem(KEY_CURRENT_CUSTOMER_ID, value, { session: true });
    storage.writeItem(KEY_CURRENT_CUSTOMER_ID, value);
  },
};

const KEY_CURRENT_WORKSPACE_ID = 'kcc.customer.currentWorkspaceId';
export const currentWorkspaceId = {
  read: (options?: CurrentCustomerIdOptions) => {
    const sessionValue = !options?.skipSession
      ? storage.readItem(KEY_CURRENT_WORKSPACE_ID, { session: true })
      : null;
    const localStorageValue = storage.readItem(KEY_CURRENT_WORKSPACE_ID);
    return sessionValue || localStorageValue || null;
  },

  write: (value: string) => {
    storage.writeItem(KEY_CURRENT_WORKSPACE_ID, value, { session: true });
    storage.writeItem(KEY_CURRENT_WORKSPACE_ID, value);
  },

  remove: () => {
    storage.removeItem(KEY_CURRENT_WORKSPACE_ID, { session: true });
    storage.removeItem(KEY_CURRENT_WORKSPACE_ID);
  },
};

// Blog Draft Facts

const getBlogFactsStorageKey = (keyType: string, projectId: string): string => {
  return `${keyType}_${projectId}`;
};

const KEY_BLOG_DRAFT_FACTS_ALL = 'blog_draft_facts_all';
export const blogFactsAll = {
  read: (projectId: string): unknown[] => {
    const key = getBlogFactsStorageKey(KEY_BLOG_DRAFT_FACTS_ALL, projectId);
    return JSON.parse(storage.readItem(key) || '[]');
  },
  write: (projectId: string, value: unknown) => {
    const key = getBlogFactsStorageKey(KEY_BLOG_DRAFT_FACTS_ALL, projectId);
    storage.writeItem(key, JSON.stringify(value));
  },
  remove: (projectId: string) => {
    const key = getBlogFactsStorageKey(KEY_BLOG_DRAFT_FACTS_ALL, projectId);
    storage.removeItem(key);
  },
};

const KEY_BLOG_DRAFT_FACTS_SELECTED = 'blog_draft_facts_selected';
export const blogFactsSelected = {
  read: (projectId: string): unknown[] => {
    const key = getBlogFactsStorageKey(KEY_BLOG_DRAFT_FACTS_SELECTED, projectId);
    return JSON.parse(storage.readItem(key) || '[]');
  },
  write: (projectId: string, value: unknown) => {
    const key = getBlogFactsStorageKey(KEY_BLOG_DRAFT_FACTS_SELECTED, projectId);
    storage.writeItem(key, JSON.stringify(value));
  },
  remove: (projectId: string) => {
    const key = getBlogFactsStorageKey(KEY_BLOG_DRAFT_FACTS_SELECTED, projectId);
    storage.removeItem(key);
  },
};

const KEY_BLOG_DRAFT_FACTS_CURSOR = 'blog_draft_facts_cursor';
export const blogFactsCursor = {
  read: (projectId: string): unknown => {
    const key = getBlogFactsStorageKey(KEY_BLOG_DRAFT_FACTS_CURSOR, projectId);
    return JSON.parse(storage.readItem(key) || 'null');
  },
  write: (projectId: string, value: unknown) => {
    const key = getBlogFactsStorageKey(KEY_BLOG_DRAFT_FACTS_CURSOR, projectId);
    storage.writeItem(key, JSON.stringify(value));
  },
  remove: (projectId: string) => {
    const key = getBlogFactsStorageKey(KEY_BLOG_DRAFT_FACTS_CURSOR, projectId);
    storage.removeItem(key);
  },
};

const KEY_PROJECT_IMPROVE_SCORE_MODE = 'ui.project.improveScoreMode';
export const projectImproveScoreMode = {
  read: (projectId: string): string | null => {
    const key = `${KEY_PROJECT_IMPROVE_SCORE_MODE}_${projectId}`;
    return storage.readItem(key) ?? null;
  },
  write: (projectId: string, value: string) => {
    const key = `${KEY_PROJECT_IMPROVE_SCORE_MODE}_${projectId}`;
    storage.writeItem(key, value);
  },
};

export const lastModified = createStringOption('kcc.system.lastModified');

export const lastUsedProjectType = createStringOption('kcc.project.lastUsedProjectType');

export const projectsFilter = createStringOption('ui.projects.filter');

export const projectsTypeFilter = createStringOption('ui.projects.type.filter');

export const projectLastUsedSavedChannel = createStringOption('ui.project.lastUsedSavedChannel');

export const projectLastUsedSavedFormat = createStringOption('ui.project.lastUsedSavedFormat');

export const lastUsedContentTaxonomyId = createStringOption(
  'kcc.project.lastUsedContentTaxonomyId'
);

export const coTutorialWasShownInSession = createBooleanOption('ui.co.tutorialWasShown', {
  session: true,
});

export const socialPostAccessToken = createStringOption('kcc.socialPostAccessToken');

export const socialPostProjectId = createStringOption('kcc.socialPostProjectId');

export const lastGeneratedScoreHintTimestamp = createStringOption(
  'onboarding.score.lastGeneratedTimestamp'
);

export const lastGeneratedPersonaHintTimestamp = createStringOption(
  'onboarding.persona.lastGeneratedTimestamp'
);

export const favoritesOnly = createBooleanOption('ui.projects.favoritesOnly');

export const sessionApplicationMode = createStringOption('system.applicationMode', {
  session: true,
});
export const sessionApplicationHost = createStringOption('system.applicationHost', {
  session: true,
});

export const signInReturnPath = createStringOption('system.signInReturnPath', {
  session: true,
});

export const documentGenerationLanguage = createStringOption('document.generationLanguage');

export const documentLocalizationLanguage = createStringOption('document.localizationLanguage');

export const developmentMode = createBooleanOption('system.developmentMode');

export const textDetailsPanelTab = createStringOption('document.textDetailsPanelTab');

export const shownPerformancePredictionsUpgradeTimestamp = createStringOption(
  'limits.performancePredictions.upgradeShownTimestamp'
);
export const wasPerformancePredictionsUpgradeShownInLast24Hours = (): boolean => {
  const lastShown = Number(shownPerformancePredictionsUpgradeTimestamp.read('0'));
  return Date.now() - lastShown < 24 * 60 * 60 * 1000;
};

export const closedPerformancePredictionsBannerTimestamp = createStringOption(
  'limits.performancePredictions.bannerClosedTimestamp'
);
export const wasPerformancePredictionsBannerClosedInLast24Hours = (): boolean => {
  const lastShown = Number(closedPerformancePredictionsBannerTimestamp.read('0'));
  return Date.now() - lastShown < 24 * 60 * 60 * 1000;
};

export const templateFoundPopupDoNotShowAgain = createBooleanOption(
  'ui.popups.templateFoundPopupDoNotShowAgain'
);
